const AUTHORIZATION_URL = `${process.env.VUE_APP_API_BASE_URL2}/api/users/autorisation`;
const REFRESH_LOCK_KEY = "taskpal_refresh_lock";
const REFRESH_LOCK_TTL_MS = 15000;
const REFRESH_WAIT_TIMEOUT_MS = 20000;
const REFRESH_POLL_INTERVAL_MS = 150;

let refreshPromise = null;

function getLanguageHeader() {
  return localStorage.getItem("language") || "en";
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function readRefreshLock() {
  try {
    const value = localStorage.getItem(REFRESH_LOCK_KEY);

    if (!value) return null;

    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

function releaseRefreshLock(owner) {
  const currentLock = readRefreshLock();

  if (currentLock?.owner === owner) {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }
}

function hasFreshAuthTokens(previousCloneToken) {
  const nextCloneToken = localStorage.getItem("w_clone_token");
  const nextAccessToken = localStorage.getItem("w_token");

  return (
    Boolean(nextAccessToken) &&
    Boolean(nextCloneToken) &&
    nextCloneToken !== previousCloneToken
  );
}

function tryAcquireRefreshLock(owner) {
  const now = Date.now();
  const currentLock = readRefreshLock();

  if (
    currentLock &&
    currentLock.expiresAt > now &&
    currentLock.owner !== owner
  ) {
    return false;
  }

  localStorage.setItem(
    REFRESH_LOCK_KEY,
    JSON.stringify({
      owner,
      expiresAt: now + REFRESH_LOCK_TTL_MS,
    })
  );

  return readRefreshLock()?.owner === owner;
}

async function waitForRefreshCompletion(previousCloneToken) {
  const startedAt = Date.now();

  while (Date.now() - startedAt < REFRESH_WAIT_TIMEOUT_MS) {
    if (hasFreshAuthTokens(previousCloneToken)) {
      return true;
    }

    const currentLock = readRefreshLock();

    if (!currentLock || currentLock.expiresAt <= Date.now()) {
      return hasFreshAuthTokens(previousCloneToken);
    }

    await sleep(REFRESH_POLL_INTERVAL_MS);
  }

  return hasFreshAuthTokens(previousCloneToken);
}

export function getAuthHeaders(extraHeaders = {}) {
  return {
    Code: "web777",
    lang: getLanguageHeader(),
    Authorization: localStorage.getItem("w_token"),
    ...extraHeaders,
  };
}

export async function refreshAuthToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const initialCloneToken = localStorage.getItem("w_clone_token");

    if (!initialCloneToken) return false;

    const lockOwner = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const lockStartedAt = Date.now();
    let lockAcquired = false;

    while (Date.now() - lockStartedAt < REFRESH_WAIT_TIMEOUT_MS) {
      if (hasFreshAuthTokens(initialCloneToken)) {
        return true;
      }

      if (tryAcquireRefreshLock(lockOwner)) {
        lockAcquired = true;
        break;
      }

      await sleep(REFRESH_POLL_INTERVAL_MS);
    }

    if (!lockAcquired) {
      return waitForRefreshCompletion(initialCloneToken);
    }

    try {
      if (hasFreshAuthTokens(initialCloneToken)) {
        return true;
      }

      const cloneToken = localStorage.getItem("w_clone_token");

      if (!cloneToken) return false;

      const res = await fetch(AUTHORIZATION_URL, {
        method: "POST",
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({ check_token: cloneToken }),
      });

      if (!res.ok) {
        return hasFreshAuthTokens(initialCloneToken);
      }

      const data = await res.json();

      if (!data?.token || !data?.email) {
        return hasFreshAuthTokens(initialCloneToken);
      }

      localStorage.setItem("w_clone_token", data.email);
      localStorage.setItem("w_token", data.token);

      return true;
    } finally {
      releaseRefreshLock(lockOwner);
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export async function authFetch(url, options = {}, config = {}) {
  const {
    retryOn401 = true,
    includeAuth = true,
    includeCode = true,
    lang = true,
  } = config;

  const headers = {
    ...(options.headers || {}),
  };

  if (includeCode && !headers.Code) {
    headers.Code = "web777";
  }

  if (lang && !headers.lang) {
    headers.lang = getLanguageHeader();
  }

  if (includeAuth) {
    headers.Authorization = localStorage.getItem("w_token");
  }

  let response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401 && retryOn401 && includeAuth) {
    const refreshed = await refreshAuthToken();

    if (refreshed) {
      response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          Authorization: localStorage.getItem("w_token"),
        },
      });
    }
  }

  return response;
}
